import Container from '../Container';
import TastingCard from './TastingCard';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { SkuEnum } from '@/types/translations/SkuEnum';
import { TastingCardData } from '@/utilites/tasting-event';

/**
 * The tastings grid: first one large, the rest three across.
 *
 * Geometry and classes are `MagazineLatestArticles`, deliberately — the two
 * pages are the same page with different contents, and a grid that almost
 * matches reads worse than one that does.
 *
 * No sorting here. The magazine sorts in the component because its API hands
 * back whatever order Strapi felt like; tastings are sorted by `startsAt` in
 * the query, ascending for what is coming and descending for what is over, and
 * re-sorting a page of a paginated list in the browser would only scramble it.
 */
export default function TastingList({
  tastings,
  loading,
  heading,
}: {
  tastings: TastingCardData[];
  loading: boolean;
  heading: string;
}): JSX.Element {
  const { translate } = useTypedTranslation<SkuEnum>('sku');

  return (
    <Container>
      <div className="tw-pt-9">
        <div className="tw-w-fit tw-pb-9">
          <h2 className="tw-font-plusJakartaSans tw-text-2xl tw-font-bold tw-text-text max-nine:tw-text-sm">
            {heading}
          </h2>
          <hr
            className="tw-m-0 tw-mt-2 tw-h-1 tw-w-4/6 tw-rounded-full tw-border-t-0 tw-border-none tw-bg-custom-gold tw-p-0 tw-opacity-100"
            aria-hidden="true"
          />{' '}
        </div>
        <div className="tw-flex tw-flex-col tw-gap-6">
          <div>
            {loading ? (
              <TastingCard tasting={null} mainCard={true} />
            ) : tastings.length <= 0 ? null : (
              <TastingCard tasting={tastings[0]} mainCard={true} />
            )}
          </div>
          <div className="tw-flex tw-flex-wrap tw-justify-between tw-gap-4 child:tw-w-[calc(33.33%-1rem)] child:tw-basis-[calc(33.33%-1rem)] max-md:child:tw-w-[calc(50%-1rem)] max-md:child:tw-basis-[calc(50%-1rem)] max-sm:child:tw-basis-full">
            {!loading && tastings.length === 0 && (
              <p className="tw-w-full tw-basis-full tw-text-slate-gray">
                {translate(SkuEnum['tasting.none_found'])}
              </p>
            )}
            {loading
              ? Array.from({ length: 3 }).map((_, index) => (
                  <TastingCard key={index} tasting={null} />
                ))
              : tastings
                  .slice(1)
                  .map(tasting => <TastingCard key={tasting.slug} tasting={tasting} />)}
          </div>
        </div>
      </div>
    </Container>
  );
}
